'use client';

import React from 'react';
import { PrivyProvider } from '@privy-io/react-auth';
import { toSolanaWalletConnectors } from '@privy-io/react-auth/solana';

interface PrivyProviderWrapperProps {
  children: React.ReactNode;
}

export default function PrivyProviderWrapper({ children }: PrivyProviderWrapperProps) {
  const appId = process.env.REACT_APP_PRIVY_APP_ID;
  const clientId = process.env.REACT_APP_PRIVY_CLIENT_ID;

  // Note: MWA registration moved to index.tsx to run before React initialization
  
  if (!appId) {
    console.error('REACT_APP_PRIVY_APP_ID is not set in environment variables');
    return <div>Privy configuration error. Please check environment variables.</div>;
  }

  return (
    <PrivyProvider
      appId={appId}
      clientId={clientId}
      config={{
        
        // Login options (wallet FIRST = visible on mobile):
        // - 'wallet': Connect external wallet (Phantom, Solflare, etc.) - PRIORITIZED FOR MOBILE
        // - 'email': Passwordless email (mobile-friendly, no app needed)
        // - 'sms': Phone number login (excellent for mobile)
        // - 'google': Universal social login
        // - 'twitter': Social login with embedded Solana wallet
        loginMethods: ['wallet', 'email', 'sms', 'google', 'twitter'],
        
        // Create embedded Solana wallets only for social logins (Twitter)
        embeddedWallets: {
          solana: {
            createOnLogin: 'users-without-wallets'
          }
        },
        
        // Configure external Solana wallets
        externalWallets: {
          solana: {
            connectors: toSolanaWalletConnectors(),
          },
        },
        
        // Configure Solana network to match backend
        solanaClusters: [
          {
            name: process.env.REACT_APP_SOLANA_NETWORK === 'mainnet' ? 'mainnet-beta' : 'devnet',
            rpcUrl: process.env.REACT_APP_SOLANA_NETWORK === 'mainnet' 
              ? 'https://api.mainnet-beta.solana.com'
              : 'https://api.devnet.solana.com'
          }
        ],
        
        // Customize appearance and behavior - Solana wallets only
        appearance: {
          theme: 'dark',
          accentColor: '#ff6b35',
          walletChainType: 'solana-only',
          // Show wallet list prominently (critical for mobile visibility)
          showWalletLoginFirst: true,
          // Explicitly list wallets to show (critical for mobile detection)
          walletList: ['detected_solana_wallets', 'phantom', 'solflare', 'backpack'],
        },
        
        // Optimize OAuth flow
        mfa: {
          noPromptOnMfaRequired: false
        }
      }}
    >
      {children}
    </PrivyProvider>
  );
} 